import { supabase } from "./supabase";

const BUCKET = "fichas";

// Converte um dataURL (ex.: canvas.toDataURL) em Blob
export function dataURLtoBlob(dataURL) {
  const [head, body] = dataURL.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Faz upload de um Blob/File e retorna o path salvo (ou null em erro)
// upsert=false é usado no formulário anônimo (não tem permissão de update)
export async function uploadToStorage(path, blob, contentType, { upsert = true } = {}) {
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      upsert,
      contentType: contentType || blob.type || "application/octet-stream",
    });
  if (error) {
    console.error("Erro upload storage:", error.message);
    throw new Error(error.message);
  }
  return path;
}

// Gera uma URL assinada temporária para visualizar/baixar um arquivo privado
export async function getSignedUrl(path, expiresIn = 3600) {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresIn);
  if (error) {
    console.error("Erro signed url:", error.message);
    return null;
  }
  return data?.signedUrl || null;
}

// Remove um ou mais arquivos do bucket
export async function removeFromStorage(paths) {
  const list = Array.isArray(paths) ? paths : [paths];
  const clean = list.filter(Boolean);
  if (clean.length === 0) return;
  await supabase.storage.from(BUCKET).remove(clean);
}

// Trecho seguro para nome de arquivo no Storage. O Supabase recusa chave com
// acento, espaço, "&" etc. ("InvalidKey") — e o registro rápido montava o nome
// do arquivo com o nome da empresa: "Auto Center São João" não subia nada.
export function chaveSegura(texto, fallback = "arquivo") {
  const s = String(texto || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
    .slice(0, 30);
  return s || fallback;
}

export const ehImagem = (file) =>
  /^image\//i.test(file?.type || "") || /\.(jpe?g|png|webp|heic|heif|gif)$/i.test(file?.name || "");

// Foto da câmera de tablet/celular chega com 5–12 MB (e em HEIC no iPad). Reduz
// para JPEG de até 1600px antes de subir: envio rápido no 4G e abre em qualquer
// navegador. Se o navegador não conseguir decodificar, sobe o original.
export async function reduzirFoto(file, lado = 1600, qualidade = 0.82) {
  if (!ehImagem(file) || /gif$/i.test(file.type || "")) return file;
  try {
    let fonte;
    let w; let h;
    if (typeof createImageBitmap === "function") {
      fonte = await createImageBitmap(file, { imageOrientation: "from-image" });
      w = fonte.width; h = fonte.height;
    } else {
      const url = URL.createObjectURL(file);
      fonte = await new Promise((ok, falha) => {
        const img = new Image();
        img.onload = () => ok(img); img.onerror = falha; img.src = url;
      });
      URL.revokeObjectURL(url);
      w = fonte.naturalWidth; h = fonte.naturalHeight;
    }
    if (!w || !h) return file;
    const escala = Math.min(1, lado / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * escala); canvas.height = Math.round(h * escala);
    canvas.getContext("2d").drawImage(fonte, 0, 0, canvas.width, canvas.height);
    if (fonte.close) fonte.close();
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/jpeg", qualidade));
    if (!blob || (blob.size >= file.size && /jpe?g$/i.test(file.type || ""))) return file;
    const base = (file.name || "foto").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

const MIME_EXT_UPLOAD = {
  "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png", "image/webp": "webp",
  "image/heic": "heic", "image/heif": "heif", "application/pdf": "pdf", "application/json": "json",
};

// Prepara qualquer anexo para o Storage: foto de câmera vira JPEG menor
// (documento precisa continuar legível — por isso 2400px por padrão) e a
// extensão sai só com [a-z0-9]. Nome de arquivo do celular às vezes vem sem
// extensão, com espaço ou acento depois do ponto — e chave inválida o Storage
// recusa inteira.
export async function prepararUpload(original, { lado = 2400, qualidade = 0.88 } = {}) {
  const file = ehImagem(original) ? await reduzirFoto(original, lado, qualidade) : original;
  const extNome = file.name?.includes(".") ? file.name.split(".").pop().toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 5) : "";
  const ext = MIME_EXT_UPLOAD[(file.type || "").toLowerCase()] || extNome || "jpg";
  const contentType = file.type || (ext === "pdf" ? "application/pdf" : "image/jpeg");
  return { file, ext, contentType };
}
